import { SKIP, visit } from "unist-util-visit";
import {
  normalizeVariableRefId,
  VAR_REF_PATTERN,
} from "@/components/SpotflowV2/Sidebar/variableRefs";

interface UnistNode {
  type: string;
  name?: string;
  value?: string;
  data?: { directiveLabel?: boolean };
  children?: UnistNode[];
}

function isParent(
  node: UnistNode | undefined | null
): node is UnistNode & { children: UnistNode[] } {
  return node != null && Array.isArray(node.children);
}

function collectText(node: UnistNode): string {
  if (node.value) return node.value;
  return (node.children ?? []).map(collectText).join("");
}

function varDirective(id: string): UnistNode {
  return {
    type: "textDirective",
    name: "var",
    children: [{ type: "text", value: id }],
  };
}

function paragraphWithPrefix(prefix: string, id: string): UnistNode {
  const children: UnistNode[] = [];
  if (prefix) children.push({ type: "text", value: prefix });
  children.push(varDirective(id));
  return { type: "paragraph", children };
}

function extraColonsFor(type: string): string {
  if (type === "containerDirective") return "::";
  if (type === "leafDirective") return ":";
  return "";
}

// A container directive holds its label in a `directiveLabel` child and its
// body in the remaining ones. Only the label is the ref; an unclosed `:::var`
// swallows the rest of the document, so the body has to survive as siblings.
function replacementFor(node: UnistNode): UnistNode[] {
  const prefix = extraColonsFor(node.type);
  if (node.type !== "containerDirective") {
    const id = normalizeVariableRefId(collectText(node));
    return [paragraphWithPrefix(prefix, id)];
  }
  const children = node.children ?? [];
  const labelIndex = children.findIndex((c) => c.data?.directiveLabel === true);
  if (labelIndex < 0) {
    const marker: UnistNode = {
      type: "paragraph",
      children: [{ type: "text", value: `${prefix}:var` }],
    };
    return [marker, ...children];
  }
  const id = normalizeVariableRefId(collectText(children[labelIndex]));
  const body = children.filter((_, i) => i !== labelIndex);
  return [paragraphWithPrefix(prefix, id), ...body];
}

function splitVarRefsInText(value: string): UnistNode[] | null {
  const matches = [...value.matchAll(new RegExp(VAR_REF_PATTERN.source, "g"))];
  if (matches.length === 0) return null;
  const parts: UnistNode[] = [];
  let last = 0;
  for (const match of matches) {
    const start = match.index ?? 0;
    if (start > last) {
      parts.push({ type: "text", value: value.slice(last, start) });
    }
    parts.push(varDirective(normalizeVariableRefId(match[1] ?? "")));
    last = start + match[0].length;
  }
  if (last < value.length) {
    parts.push({ type: "text", value: value.slice(last) });
  }
  return parts;
}

// remark-directive promotes a whole-line `::var[id]` to a block leaf directive
// and leaves every other `::var[id]` as plain text. Either way the chip is lost
// on remount after the user types `:` immediately before one. Fold both back
// into a text `:` plus a `textDirective` named `var`.
export function normalizeVarDirectives() {
  return (tree: unknown) => {
    visit(tree as Parameters<typeof visit>[0], (node, index, parent) => {
      const n = node as UnistNode;
      const p = parent as UnistNode | undefined;
      if (n.type !== "leafDirective" && n.type !== "containerDirective") {
        return;
      }
      if (n.name !== "var") return;
      if (!isParent(p) || index == null) return;
      p.children.splice(index, 1, ...replacementFor(n));
      // Resume on the sibling after the chip so a container body still gets
      // walked — it can hold `::var` of its own.
      return [SKIP, index + 1];
    });

    // Code spans and fences are leaf nodes carrying a raw `value`, so this
    // walk never reaches inside one and cannot chip-ify their contents.
    visit(
      tree as Parameters<typeof visit>[0],
      "text",
      (node, index, parent) => {
        const p = parent as UnistNode | undefined;
        if (!isParent(p) || index == null) return;
        const value = (node as UnistNode).value;
        if (!value) return;
        const parts = splitVarRefsInText(value);
        if (!parts) return;
        p.children.splice(index, 1, ...parts);
        return [SKIP, index + parts.length];
      }
    );
  };
}

// remark-stringify escapes a colon that opens a block, so a chip the user
// prefixed with `:` serializes as `\::var[id]`. That backslash is valid
// markdown but it rewrites the stored value the moment a drawer opens, and it
// shows up verbatim on every surface that renders the raw string (node cards).
// Drop it — the parse side folds a bare `::var[id]` back into text plus chip.
// An even run of backslashes is a literal escaped backslash, so leave it be.
export function unescapeVarDirectiveMarkers(markdown: string): string {
  return markdown.replace(
    /(\\*)(:+var\[)/g,
    (raw, slashes: string, marker: string) =>
      slashes.length % 2 === 1 ? `${slashes.slice(0, -1)}${marker}` : raw
  );
}
